import { ThemeExposure } from '@api/api/theme-overview';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { TableCell } from '@design/components/table';

import styles from './IndustryThemesTable.module.scss';
import type { IndustryThemeTimeframe } from './useIndustryThemeMetrics';
import { useIndustryThemeMetrics } from './useIndustryThemeMetrics';
import type {
  IndustryThemeRowData,
  IndustryThemesTableColumn,
} from './useIndustryThemesColumns';

export interface IndustryThemeRowProps {
  themeExposure: ThemeExposure;
  timeframe: IndustryThemeTimeframe;
  columns: IndustryThemesTableColumn[];
  onClick?: () => void;
}

export const IndustryThemeRow = ({
  themeExposure,
  timeframe,
  columns,
  onClick,
}: IndustryThemeRowProps) => {
  const metrics = useIndustryThemeMetrics(themeExposure.theme.id, timeframe);

  if (metrics.isLoading) {
    return (
      <tr>
        <TableCell
          className={styles.cell}
          colspan={columns.length}
          isNumber={false}
        >
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '25px', borderRadius: '8px' },
              },
            ]}
          />
        </TableCell>
      </tr>
    );
  }

  const row: IndustryThemeRowData = {
    themeId: themeExposure.theme.id,
    rank: themeExposure.relationship.rank,
    name: themeExposure.theme.name,
    logoUrl: themeExposure.theme.logo_url,
    reportedCount: metrics.reportedCount,
    totalCount: metrics.totalCount,
    epsStat: metrics.epsStat,
    revenueStat: metrics.revenueStat,
    guidanceStat: metrics.guidanceStat,
    medianT1: metrics.medianT1,
    medianT2: metrics.medianT2,
    leadingDriver: metrics.leadingDriver,
    isLoading: metrics.isLoading,
  };

  return (
    <tr onClick={onClick}>
      {columns.map(column => (
        <TableCell
          key={column.id}
          className={styles.cell}
          alignment={column.alignment ?? 'left'}
          isNumber={column.id === 'rank'}
        >
          {column.cell(row)}
        </TableCell>
      ))}
    </tr>
  );
};
